"use client"

import { useState, useEffect, useCallback } from "react"

const HIDE_PERSONAL_DATA_KEY = "hidePersonalData"

function getStored(): boolean {
    if (typeof window === "undefined") return false
    return localStorage.getItem(HIDE_PERSONAL_DATA_KEY) === "true"
}

export function useHidePersonalData(): [boolean, (value: boolean) => void] {
    const [hidePersonalData, setHidePersonalDataState] = useState(false)

    // Sync from localStorage after mount so client always has correct value (avoids hydration mismatch)
    useEffect(() => {
        if (typeof window === "undefined") return
        setHidePersonalDataState(getStored())
    }, [])

    useEffect(() => {
        if (typeof window === "undefined") return

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key !== HIDE_PERSONAL_DATA_KEY) return
            setHidePersonalDataState(e.newValue === "true")
        }

        const handleCustomChange = () => {
            setHidePersonalDataState(getStored())
        }

        window.addEventListener("storage", handleStorageChange)
        window.addEventListener("hidePersonalDataChanged", handleCustomChange)

        return () => {
            window.removeEventListener("storage", handleStorageChange)
            window.removeEventListener("hidePersonalDataChanged", handleCustomChange)
        }
    }, [])

    const setHidePersonalData = useCallback((value: boolean) => {
        setHidePersonalDataState(value)
        if (typeof window !== "undefined") {
            localStorage.setItem(HIDE_PERSONAL_DATA_KEY, String(value))
            window.dispatchEvent(new Event("hidePersonalDataChanged"))
        }
    }, [])

    return [hidePersonalData, setHidePersonalData]
}
